import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Stack,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Savings,
  Paid,
  PictureAsPdf,
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import { getCurrentYearCashflow } from '../../../services/request/Dashboard';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { PageHeader } from '../../../shared-components/ui';

interface CurrentYearCashflow {
  year: number;
  principalCollected: number;
  interestCollected: number;
  lateFeesCollected: number;
  totalCashInflow: number;
  newLoanDisbursements: number;
  totalCashOutflow: number;
  netCashFlow: number;
  totalInterestEarnedInYear: number;
}

const AnnualFinancialSummary: React.FC = () => {
  const [data, setData] = useState<CurrentYearCashflow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getCurrentYearCashflow();
        const payload = response.data || response;
        setData({
          year: payload.year || new Date().getFullYear(),
          principalCollected: payload.principalCollected || 0,
          interestCollected: payload.interestCollected || 0,
          lateFeesCollected: payload.lateFeesCollected || 0,
          totalCashInflow: payload.totalCashInflow || 0,
          newLoanDisbursements: payload.newLoanDisbursements || 0,
          totalCashOutflow: payload.totalCashOutflow || 0,
          netCashFlow: payload.netCashFlow || 0,
          totalInterestEarnedInYear: payload.totalInterestEarnedInYear || 0,
        });
      } catch (e: any) {
        setError(
          e?.message || 'Failed to load annual financial summary. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const formatAmountForPdf = (value: number) =>
    `INR ${value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const handleExportPdf = () => {
    if (!data) return;

    const doc = new jsPDF();
    let y = 20;

    doc.setFontSize(16);
    doc.text(`Annual Financial Summary - ${data.year}`, 14, y);
    y += 10;

    doc.setFontSize(12);
    doc.text(`Annual Revenue (Interest + Late Fees): ${formatAmountForPdf(
      data.interestCollected + data.lateFeesCollected
    )}`, 14, y);
    y += 8;

    doc.text(`Interest Collected: ${formatAmountForPdf(data.interestCollected)}`, 14, y);
    y += 8;
    doc.text(`Late Fees Collected: ${formatAmountForPdf(data.lateFeesCollected)}`, 14, y);
    y += 12;

    doc.text(`Total Cash Inflow: ${formatAmountForPdf(data.totalCashInflow)}`, 14, y);
    y += 8;
    doc.text(`Total Cash Outflow (Loans Disbursed): ${formatAmountForPdf(
      data.totalCashOutflow
    )}`, 14, y);
    y += 12;

    doc.text(`Net Cash Flow: ${formatAmountForPdf(data.netCashFlow)}`, 14, y);
    y += 8;
    doc.text(`Principal Collected: ${formatAmountForPdf(data.principalCollected)}`, 14, y);

    doc.save(`annual-financial-summary-${data.year}.pdf`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <PageHeader
          eyebrow="Lending · Reports"
          title="Annual financial summary"
          subtitle="Financial year cashflow, revenue, and net position."
        />
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const annualRevenue = data.interestCollected + data.lateFeesCollected;
  const netIcon = data.netCashFlow >= 0 ? <TrendingUp /> : <TrendingDown />;

  return (
    <Box>
      <PageHeader
        eyebrow="Lending · Reports"
        title="Annual financial summary"
        subtitle={`Financial year ${data.year} — cashflow, revenue, and net position.`}
        actions={
          <Button
            variant="outlined"
            size="small"
            startIcon={<PictureAsPdf />}
            onClick={handleExportPdf}
          >
            Export PDF
          </Button>
        }
      />

    <Card
      elevation={3}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Chip
          icon={netIcon}
          label={`Net cash flow: ${formatCurrency(data.netCashFlow)}`}
          sx={{
            bgcolor: 'rgba(255,255,255,0.15)',
            color: 'inherit',
            fontWeight: 600,
          }}
        />
      </Box>

      <CardContent>
        <Grid container spacing={3}>
          {/* Revenue Block */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Paid sx={{ mr: 1, color: 'success.main' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Annual Revenue (Interest + Late Fees)
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {formatCurrency(annualRevenue)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Interest: {formatCurrency(data.interestCollected)} · Late fees:{' '}
              {formatCurrency(data.lateFeesCollected)}
            </Typography>
          </Grid>

          {/* Simple “Balance sheet” style view */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccountBalance sx={{ mr: 1, color: 'info.main' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Cash In / Out
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Total inflow
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {formatCurrency(data.totalCashInflow)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total outflow (loans disbursed)
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {formatCurrency(data.totalCashOutflow)}
            </Typography>
          </Grid>

          {/* Net position */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Savings sx={{ mr: 1, color: data.netCashFlow >= 0 ? 'success.main' : 'error.main' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Net Position
              </Typography>
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: data.netCashFlow >= 0 ? 'success.main' : 'error.main',
              }}
            >
              {formatCurrency(data.netCashFlow)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Principal collected: {formatCurrency(data.principalCollected)}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
    </Box>
  );
};

export default AnnualFinancialSummary;

